import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { normalizeGroupVideos, isYoutubeVideoUrl } from '@/utils/groupVideos'

export function useQuestionBank() {
  const store = useStore()
  const searchQuery = ref('')

  // Computed properties - store에서 페이지네이션 상태 가져오기
  const questionGroups = computed(() => store.getters['questionBank/getQuestionGroups'])
  const currentGroup = computed(() => store.getters['questionBank/getCurrentGroup'])
  const searchResults = computed(() => store.getters['questionBank/getSearchResults'])
  const filters = computed(() => store.getters['questionBank/getFilters'])
  const filteredGroups = computed(() => store.getters['questionBank/getFilteredGroups'])
  const isLoading = computed(() => store.getters['questionBank/isLoading'])
  const error = computed(() => store.getters['questionBank/getError'])
  const totalGroups = computed(() => store.getters['questionBank/getTotalGroups'])
  const currentPage = computed(() => store.getters['questionBank/getCurrentPage'] || 1)
  const itemsPerPage = computed(() => store.getters['questionBank/getItemsPerPage'] || 20)
  const statistics = computed(() => store.getters['questionBank/getStatistics'])

  // Actions
  const fetchQuestionGroups = async (params = {}) => {
    return await store.dispatch('questionBank/fetchQuestionGroups', {
      page: currentPage.value,
      limit: itemsPerPage.value,
      ...params
    })
  }

  const fetchStatistics = async () => {
    return await store.dispatch('questionBank/fetchStatistics')
  }

  const fetchQuestionGroup = async (groupId) => {
    return await store.dispatch('questionBank/fetchQuestionGroup', groupId)
  }

  const createQuestionGroup = async (groupData) => {
    return await store.dispatch('questionBank/createQuestionGroup', groupData)
  }

  const updateQuestionGroup = async (groupId, groupData) => {
    return await store.dispatch('questionBank/updateQuestionGroup', { groupId, groupData })
  }

  const deleteQuestionGroup = async (groupId) => {
    return await store.dispatch('questionBank/deleteQuestionGroup', groupId)
  }

  const searchQuestions = async () => {
    if (!searchQuery.value.trim()) return
    
    return await store.dispatch('questionBank/searchQuestions', {
      query: searchQuery.value,
      ...filters.value
    })
  }

  const setFilters = (newFilters) => {
    store.dispatch('questionBank/setFilters', newFilters)
  }

  const resetFilters = () => {
    store.dispatch('questionBank/resetFilters')
    searchQuery.value = ''
  }

  // Pagination helpers - store의 currentPage를 직접 업데이트
  const setCurrentPage = (page) => {
    store.commit('questionBank/SET_CURRENT_PAGE', page)
  }

  const nextPage = () => {
    const newPage = currentPage.value + 1
    setCurrentPage(newPage)
    fetchQuestionGroups({ page: newPage, filters: filters.value })
  }

  const previousPage = () => {
    if (currentPage.value > 1) {
      const newPage = currentPage.value - 1
      setCurrentPage(newPage)
      fetchQuestionGroups({ page: newPage, filters: filters.value })
    }
  }

  const goToPage = (page) => {
    setCurrentPage(page)
    fetchQuestionGroups({ page, filters: filters.value })
  }

  // Question validation
  const validateQuestion = (question) => {
    const errors = []
    
    //if (!question.questionText || question.questionText.trim().length < 5) {
    //  errors.push('문제 내용은 최소 5자 이상이어야 합니다.')
    //}
    
    if (!question.level || !['low', 'middle', 'high'].includes(question.level)) {
      errors.push('난이도를 선택해주세요.')
    }
    
    if (question.choices && question.choices.length > 0) {
      const hasAnswer = question.choices.some(c => c.isAnswer)
      if (!hasAnswer) {
        errors.push('정답을 하나 이상 선택해주세요.')
      }
      
      const hasEmptyChoice = question.choices.some(c => {
        // 이미지가 있으면 텍스트 없어도 OK, 없으면 텍스트 필요
        const hasImage = c.image && (c.image.file || c.image.url)
        const hasText = c.choiceText && c.choiceText.trim() !== ''
        return !hasImage && !hasText
      })
      if (hasEmptyChoice) {
        errors.push('모든 선택지에 내용 또는 이미지를 입력해주세요.')
      }
    }
    
    if (question.score && (question.score < 1 || question.score > 100)) {
      errors.push('점수는 1점에서 100점 사이여야 합니다.')
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Group validation
  const validateQuestionGroup = (group) => {
    const errors = []
    
    //if (!group.instruction || group.instruction.trim().length < 10) {
    //  errors.push('문제 지시사항은 최소 10자 이상이어야 합니다.')
    //}
    
    if (!group.skill || !['reading', 'grammar', 'vocabulary', 'listening', 'speaking', 'writing'].includes(group.skill)) {
      errors.push('스킬 유형을 선택해주세요.')
    }
    
    if (!group.textBook || group.textBook.trim() === '') {
      errors.push('교재를 선택해주세요.')
    }
    
    if (group.questions && group.questions.length === 0) {
      errors.push('최소 1개 이상의 문제를 추가해주세요.')
    }

    // 그룹 동영상 — 등록은 YouTube 주소만(2026-10-13 결정, 화면 규칙·강사 화면과 같음). 성취도 백엔드 검증은 http(s) 그대로이고
    // 아니면 422 로 그룹 저장 전체를 거절한다. 오류 문구의 '동영상' 으로 폼이 errors.groupVideos 에 매핑한다(QuestionGroupForm.handleSubmit)
    const badVideo = normalizeGroupVideos(group.groupVideos).find(video => !isYoutubeVideoUrl(video.url))
    if (badVideo) {
      errors.push(`동영상은 YouTube 주소만 등록할 수 있습니다 (https://www.youtube.com/watch?v=… 또는 https://youtu.be/…): ${badVideo.url}`)
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Generate unique question ID (21 digits)
  const generateQuestionId = () => {
    const timestamp = Date.now().toString()
    const random = Math.random().toString(36).substr(2, 9)
    const combined = timestamp + random
    
    // Ensure we have exactly 21 characters
    if (combined.length >= 21) {
      return combined.substr(0, 21)
    } else {
      const paddingLength = 21 - combined.length
      const padding = '0'.repeat(paddingLength)
      return combined + padding
    }
  }

  // Format skill name for display
  const formatSkillName = (skill) => {
    const skillNames = {
      reading: '읽기',
      grammar: '문법',
      vocabulary: '어휘',
      listening: '듣기',
      speaking: '말하기',
      writing: '쓰기'
    }
    return skillNames[skill] || skill
  }

  // Format level name for display
  const formatLevelName = (level) => {
    const levelNames = {
      low: '하',
      middle: '중',
      high: '상'
    }
    return levelNames[level] || level
  }

  return {
    // State
    questionGroups,
    currentGroup,
    searchResults,
    filters,
    filteredGroups,
    isLoading,
    error,
    totalGroups,
    statistics,
    searchQuery,
    currentPage,
    itemsPerPage,

    // Actions
    fetchQuestionGroups,
    fetchStatistics,
    fetchQuestionGroup,
    createQuestionGroup,
    updateQuestionGroup,
    deleteQuestionGroup,
    searchQuestions,
    setFilters,
    resetFilters,

    // Pagination
    setCurrentPage,
    nextPage,
    previousPage,
    goToPage,

    // Validation
    validateQuestion,
    validateQuestionGroup,

    // Utilities
    generateQuestionId,
    formatSkillName,
    formatLevelName
  }
}